import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { point } from "@turf/helpers";

import { useQuoteForm, useMapbox } from "./hooks";
import {
  QuoteHeader,
  CustomerSection,
  AdditionalPropertiesSection,
  PropertyMapSection,
  LineItemsSection,
  ScheduleNotesSection,
  QuoteSummary,
  CustomItemDialog,
  AddAreaDialog,
} from "./components";
import { Card, CardContent } from "@/components/ui/card";
import SendQuoteEmailDialog from "@/components/dialogs/SendQuoteEmailDialog";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const QuoteBuilderPage = () => {
  const form = useQuoteForm();
  
  const mapbox = useMapbox({
    polygons: form.polygons,
    setPolygons: form.setPolygons,
    propertyAddress: form.propertyAddress,
    setPropertyAddress: form.setPropertyAddress,
    setParcelPin: form.setParcelPin,
  });

  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [customItem, setCustomItem] = useState({
    service_name: "",
    description: "",
    quantity: 1,
    unit: "each",
    unit_price: 0,
  });
  const [pendingSectionHeaderId, setPendingSectionHeaderId] = useState(null);
  const [newPropertyAddress, setNewPropertyAddress] = useState("");
  const [wrongPropertyMode, setWrongPropertyMode] = useState(false);
  const [skipMap, setSkipMap] = useState(false);

  useEffect(() => {
    form.fetchInitialData();
    if (form.isEditing) {
      form.fetchQuote();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.quoteId]);

  useEffect(() => {
    if (mapbox.mapContainer.current && !mapbox.map.current && MAPBOX_TOKEN && (form.propertyAddress || form.polygons.length > 0) && !skipMap) {
      mapbox.initializeMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.propertyAddress, form.polygons.length, skipMap]);

  // When a PIN lookup returns a result, fly the map to the parcel
  useEffect(() => {
    if (form.pinLookupResult && form.pinLookupResult.centroid) {
      const { lat, lon } = form.pinLookupResult.centroid;
      if (mapbox.map.current) {
        mapbox.goToCoordinates(lat, lon);
      } else if (mapbox.mapContainer.current && MAPBOX_TOKEN) {
        mapbox.initializeMap();
        // After map initializes, it will auto-fetch parcel data for the address
      }
      form.setPinLookupResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pinLookupResult]);

  const handleAddLineItem = (serviceId, sectionHeaderId = null) => {
    const result = form.addLineItem(serviceId, sectionHeaderId);
    if (result === "custom") {
      setPendingSectionHeaderId(sectionHeaderId);
      setShowCustomDialog(true);
    }
  };

  const addCustomLineItem = () => {
    if (!customItem.service_name.trim()) {
      toast.error("Please enter a service name");
      return;
    }

    const newItem = {
      id: crypto.randomUUID(),
      service_name: customItem.service_name,
      description: customItem.description || "",
      quantity: customItem.quantity || 1,
      unit: customItem.unit || "each",
      unit_price: customItem.unit_price || 0,
      total: (customItem.quantity || 1) * (customItem.unit_price || 0),
    };

    form.setLineItems((prev) => {
      if (!pendingSectionHeaderId) return [...prev, newItem];
      const headerIdx = prev.findIndex((i) => i.id === pendingSectionHeaderId);
      if (headerIdx === -1) return [...prev, newItem];
      let insertIdx = prev.length;
      for (let i = headerIdx + 1; i < prev.length; i++) {
        if (prev[i].is_section_header) { insertIdx = i; break; }
      }
      const next = [...prev];
      next.splice(insertIdx, 0, newItem);
      return next;
    });
    setShowCustomDialog(false);
    setPendingSectionHeaderId(null);
    setCustomItem({ service_name: "", description: "", quantity: 1, unit: "each", unit_price: 0 });
    toast.success("Custom line item added");
  };

  const handleAddProperty = async () => {
    if (!newPropertyAddress.trim()) return;
    
    try {
      const response = await axios.post(`${API}/parcels/by-address`, { address: newPropertyAddress });
      const data = response.data;
      
      let pin = "";
      let formattedAddress = newPropertyAddress;
      let parcelGeometry = null;
      let parcelCenter = null;
      
      if (data.features && data.features.length > 0) {
        const geocoded = data.geocoded;
        if (geocoded && geocoded.lat && geocoded.lon) {
          const searchPoint = point([geocoded.lon, geocoded.lat]);
          parcelCenter = [geocoded.lon, geocoded.lat];
          for (const feature of data.features) {
            if (feature.geometry?.type === "Polygon" && booleanPointInPolygon(searchPoint, feature)) {
              const props = feature.properties || {};
              pin = props.parno || "";
              parcelGeometry = feature.geometry;
              const street = props.siteadd || "";
              if (street) {
                formattedAddress = [
                  street.toUpperCase(),
                  geocoded.city?.toUpperCase(),
                  `${geocoded.state || "NC"} ${geocoded.zipcode || ""}`
                ].filter(Boolean).join(", ");
              }
              break;
            }
          }
        }
      }
      
      const newProp = { 
        address: formattedAddress, 
        pin,
        geometry: parcelGeometry,
        center: parcelCenter
      };
      form.setAdditionalProperties(prev => [...prev, newProp]);
      setNewPropertyAddress("");
      toast.success("Property added");
    } catch (error) {
      form.setAdditionalProperties(prev => [...prev, { address: newPropertyAddress, pin: "", geometry: null, center: null }]);
      setNewPropertyAddress("");
      toast.success("Property added (parcel data unavailable)");
    }
  };

  const handleRemoveProperty = (index) => {
    form.setAdditionalProperties(prev => prev.filter((_, i) => i !== index));
    toast.info("Property removed");
  };

  const handleCalculateDistance = async (address) => {
    await form.calculateMobilizationFee(address);
    
    if (mapbox.map.current && MAPBOX_TOKEN) {
      try {
        const geoResponse = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${MAPBOX_TOKEN}&limit=1`
        );
        const data = await geoResponse.json();
        if (data.features && data.features.length > 0) {
          mapbox.map.current.flyTo({ center: data.features[0].center, zoom: 17 });
          setTimeout(() => mapbox.fetchParcelData(address), 500);
        }
      } catch (e) {
        console.error("Failed to center map:", e);
      }
    }
  };

  const subtotal = form.calculateSubtotal();

  return (
    <div className="min-h-screen bg-zinc-50/50" data-testid="quote-builder-page">
      <QuoteHeader
        isEditing={form.isEditing}
        saving={form.saving}
        onBack={() => form.navigate("/")}
        onSave={form.handleSave}
        onSend={form.isEditing ? () => {
          if (!form.selectedCustomer?.email) {
            toast.error("Customer email is missing — add one and save first");
            return;
          }
          setSendDialogOpen(true);
        } : null}
        canSend={!!(form.isEditing && form.quoteId && form.selectedCustomer?.email)}
        status={form.quoteStatus}
        signature={form.quoteSignature}
      />

      <main className="max-w-7xl mx-auto p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white border border-zinc-200 shadow-sm rounded-sm">
              <CustomerSection
                customers={form.customers}
                loadingCustomers={form.loadingCustomers}
                selectedCustomer={form.selectedCustomer}
                onCustomerSelect={form.handleCustomerSelect}
                propertyAddress={form.propertyAddress}
                onPropertyAddressChange={form.setPropertyAddress}
                onCalculateDistance={handleCalculateDistance}
                calculatingDistance={form.calculatingDistance}
                baseLocation={form.baseLocation}
                distanceInfo={form.distanceInfo}
              />
              <CardContent className="px-6 pb-6 pt-0">
                <AdditionalPropertiesSection
                  additionalProperties={form.additionalProperties}
                  newPropertyAddress={newPropertyAddress}
                  onNewPropertyAddressChange={setNewPropertyAddress}
                  onAddProperty={handleAddProperty}
                  onRemoveProperty={handleRemoveProperty}
                />
              </CardContent>
            </Card>

            <PropertyMapSection
              mapContainerRef={mapbox.mapContainer}
              mapRef={mapbox.map}
              propertyAddress={form.propertyAddress}
              polygons={form.polygons}
              totalAcres={form.totalAcres}
              isDrawing={mapbox.isDrawing}
              onStartDrawing={mapbox.startDrawing}
              onCancelDrawing={mapbox.cancelDrawing}
              onRemovePolygon={mapbox.removePolygon}
              onRebufferTrail={mapbox.rebufferTrail}
              onRenamePolygon={form.renamePolygon}
              drawMode={mapbox.drawMode}
              onDrawModeChange={mapbox.setDrawMode}
              trailWidthFeet={mapbox.trailWidthFeet}
              onTrailWidthChange={mapbox.setTrailWidthFeet}
              followParcelLine={mapbox.followParcelLine}
              onFollowParcelLineChange={mapbox.setFollowParcelLine}
              boundaryChoice={mapbox.boundaryChoice}
              wrongPropertyMode={wrongPropertyMode}
              onWrongPropertyModeChange={setWrongPropertyMode}
              onSkipMap={setSkipMap}
              onGoToCoordinates={mapbox.goToCoordinates}
              showWetlands={mapbox.showWetlands}
              onToggleWetlands={mapbox.toggleWetlands}
              onReloadCustomLayers={mapbox.reloadCustomLayers}
            />

            <LineItemsSection
              lineItems={form.lineItems}
              services={form.services}
              polygons={form.polygons}
              onLinkArea={form.linkAreaToItem}
              onAddLineItem={handleAddLineItem}
              onAddSection={form.addSection}
              onUpdateLineItem={form.updateLineItem}
              onRemoveLineItem={form.removeLineItem}
              onMoveSection={form.moveSectionBy}
              onMoveItem={form.moveItemBy}
            />

            <ScheduleNotesSection
              expectedStartDate={form.expectedStartDate}
              onExpectedStartDateChange={form.setExpectedStartDate}
              startDateType={form.startDateType}
              onStartDateTypeChange={form.setStartDateType}
              validDays={form.validDays}
              onValidDaysChange={form.setValidDays}
              notes={form.notes}
              onNotesChange={form.setNotes}
              notesImage={form.notesImage}
              onNotesImageChange={form.setNotesImage}
              quoteId={form.quoteId}
              quotePdfFilename={form.quotePdfFilename}
              onQuotePdfChange={form.setQuotePdfFilename}
            />
          </div>

          <div className="space-y-6">
            <QuoteSummary
              lineItems={form.lineItems}
              subtotal={subtotal}
              totalAcres={form.totalAcres}
              polygons={form.polygons}
              selectedCustomer={form.selectedCustomer}
              propertyAddress={form.propertyAddress}
              parcelPin={form.parcelPin}
              requireFullPayment={form.requireFullPayment}
              onRequireFullPaymentChange={form.setRequireFullPayment}
              discountType={form.discountType}
              discountValue={form.discountValue}
              onDiscountTypeChange={form.setDiscountType}
              onDiscountValueChange={form.setDiscountValue}
              pricingMode={form.pricingMode}
              onPricingModeChange={form.setPricingMode}
              lumpSumAmount={form.lumpSumAmount}
              onLumpSumAmountChange={form.setLumpSumAmount}
            />
          </div>
        </div>
      </main>

      <CustomItemDialog
        open={showCustomDialog}
        onOpenChange={setShowCustomDialog}
        customItem={customItem}
        onCustomItemChange={setCustomItem}
        onAddCustomItem={addCustomLineItem}
      />

      <AddAreaDialog
        open={mapbox.showAddAreaDialog}
        onOpenChange={mapbox.setShowAddAreaDialog}
        pendingPolygon={mapbox.pendingPolygon}
        onConfirm={mapbox.confirmAddArea}
      />

      {form.isEditing && form.quoteId && (
        <SendQuoteEmailDialog
          open={sendDialogOpen}
          onOpenChange={setSendDialogOpen}
          quote={{
            id: form.quoteId,
            quote_number: form.quoteStatus ? undefined : undefined,
            customer_email: form.selectedCustomer?.email,
            customer_name: form.selectedCustomer?.name,
          }}
          onSent={() => {
            // Refresh the loaded quote so the locked-banner & status badge reflect "sent"
            form.fetchQuote?.();
          }}
        />
      )}
    </div>
  );
};

export default QuoteBuilderPage;
